import dayjs from 'dayjs';

import { getLocalStorage } from '@/tools/localStorage';

// 初始化GA
export const initGoogleAnalytics = ({ googleId }) => {
    if (typeof document !== 'undefined') {
        // 创建外部脚本
        const withHeadScript = document.createElement('script');
        withHeadScript.async = true;
        withHeadScript.src = `https://www.googletagmanager.com/gtag/js?id=${googleId}`;
        withHeadScript.setAttribute('data-analytics', 'google-analytics-external');

        // 创建内联脚本
        const inlineScript = document.createElement('script');
        inlineScript.setAttribute('data-analytics', 'google-analytics-inline');
        inlineScript.text = `
            window.dataLayer = window.dataLayer || [];
            function gtag() {
                dataLayer.push(arguments);
            }
            gtag('js', new Date());
            gtag('config', '${googleId}');
        `;
        inlineScript.setAttribute('type', 'text/javascript');

        // 按顺序插入脚本：先外部脚本，后内联脚本
        document.head.insertBefore(withHeadScript, document.head.firstChild);
        document.head.insertBefore(inlineScript, withHeadScript.nextSibling);
    }
};

const getEventMapping = (eventName, domain) => {
    const mappings = {
        DisAdsClick: {
            'm.gigjartwo.today': 'AW-16666701235/IDH9CJ3t-dUZELPbpos-',
            'nanimobquiz.xyz': 'AW-16666701235/O_BPCMqLxtwZELPbpos-',
            'ashdaiam.club': 'AW-16742737735/DmqJCL_k1d8ZEMfOx68-',
            'teshhcon.work': 'AW-16742737735/B5IWCJjq1d8ZEMfOx68-',
            'b.evabug.top': 'AW-16666701235/LhZZCPPkzOAZELPbpos-',
            'a.skasta.info': 'AW-16666701235/9QnGCNTzzeAZELPbpos-',
            'a.jaioffgame.top': 'AW-16666701235/G66aCLSVzuAZELPbpos-',
            'a.autsen.space': 'AW-16666701235/j099CNbQ1-AZELPbpos-',
            'a.glisaeapk.today': 'AW-16666701235/3RCJCIers-YZELPbpos-',
            'c.skawegcoupon.xyz': 'AW-16666701235/FNwRCNPWmPMZELPbpos-',
            'ianamtchef.space': 'AW-16666701235/MP-fCPTKkfwZELPbpos-',
            'zoetragame.xyz': 'AW-16666701235/zvCLCKqE_aAaELPbpos-'
        },
        AdsImpression: {
            'm.gigjartwo.today': 'AW-16666701235/SxJtCJrt-dUZELPbpos-',
            'nanimobquiz.xyz': 'AW-16666701235/XhQWCM2LxtwZELPbpos-',
            'ashdaiam.club': 'AW-16742737735/GlW5CLzk1d8ZEMfOx68-',
            'teshhcon.work': 'AW-16742737735/wmRWCOHo1d8ZEMfOx68-',
            'b.evabug.top': 'AW-16666701235/YwpSCPDkzOAZELPbpos-',
            'a.skasta.info': 'AW-16666701235/AV8gCNHzzeAZELPbpos-',
            'a.jaioffgame.top': 'AW-16666701235/iISrCLGVzuAZELPbpos-',
            'a.autsen.space': 'AW-16666701235/Qmg4CNPQ1-AZELPbpos-',
            'a.glisaeapk.today': 'AW-16666701235/4SK0CISrs-YZELPbpos-',
            'c.skawegcoupon.xyz': 'AW-16666701235/0gbzCNDWmPMZELPbpos-',
            'ianamtchef.space': 'AW-16666701235/_pXpCIXNkfwZELPbpos-',
            'zoetragame.xyz': 'AW-16666701235/bLXcCMuE_aAaELPbpos-'
        }
    };
    return mappings[eventName] ? mappings[eventName][domain] : null;
};

const getBrowserInfo = () => {
    if (typeof window === 'undefined') return null;

    const ua = navigator.userAgent;
    let browserInfo = {
        ua: ua, // 完整的UA信息
        device: 'unknown', // 设备类型：pc、tablet、mobile
        device_type: 'unknown', // 具体设备型号
        os: 'unknown', // 操作系统
        browser_type: 'unknown' // 浏览器类型和版本
    };

    // 检测设备类型
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
        browserInfo.device = 'tablet';
    } else if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated/i.test(ua)) {
        browserInfo.device = 'mobile';
    } else {
        browserInfo.device = 'pc';
    }

    // 检测操作系统
    if (/windows/i.test(ua)) {
        browserInfo.os = 'Windows';
    } else if (/macintosh|mac os x/i.test(ua)) {
        browserInfo.os = 'MacOS';
    } else if (/android/i.test(ua)) {
        browserInfo.os = 'Android';
    } else if (/iphone|ipad|ipod/i.test(ua)) {
        browserInfo.os = 'iOS';
    } else if (/linux/i.test(ua)) {
        browserInfo.os = 'Linux';
    }

    // 检测具体设备型号
    const deviceMatch = ua.match(/\(([^)]+)\)/);
    if (deviceMatch) {
        browserInfo.device_type = deviceMatch[1];
    }

    // 检测浏览器类型和版本
    if (/chrome/i.test(ua)) {
        const version = ua.match(/chrome\/(\d+)/i);
        browserInfo.browser_type = `Chrome${version ? version[1] : ''}`;
    } else if (/firefox/i.test(ua)) {
        const version = ua.match(/firefox\/(\d+)/i);
        browserInfo.browser_type = `Firefox${version ? version[1] : ''}`;
    } else if (/safari/i.test(ua)) {
        const version = ua.match(/version\/(\d+)/i);
        browserInfo.browser_type = `Safari${version ? version[1] : ''}`;
    } else if (/edge/i.test(ua)) {
        const version = ua.match(/edge\/(\d+)/i);
        browserInfo.browser_type = `Edge${version ? version[1] : ''}`;
    } else if (/opera/i.test(ua)) {
        const version = ua.match(/opera\/(\d+)/i);
        browserInfo.browser_type = `Opera${version ? version[1] : ''}`;
    } else if (/msie|trident/i.test(ua)) {
        const version = ua.match(/(?:msie |rv:)(\d+)/i);
        browserInfo.browser_type = `IE${version ? version[1] : ''}`;
    }

    return browserInfo;
};

const getRegion = async () => {
    try {
        // 方法1：通过浏览器语言获取（不太准确，但速度快）
        const getBrowserRegion = () => {
            if (typeof window === 'undefined') return null;
            const language = navigator.language || navigator.userLanguage;
            // 返回语言代码的国家部分，如 'en-US' 返回 'US'
            return language.split('-')[1] || language;
        };

        // 方法2：通过IP获取（更准确，但需要API调用）
        const getIPRegion = async () => {
            try {
                // 可以选择以下任一API：
                // const response = await fetch('https://ipapi.co/json/');
                // const response = await fetch('https://api.ipify.org?format=json');
                const response = await fetch('https://api.ip.sb/geoip');
                const data = await response.json();
                return data.country_code; // 返回两位国家代码，如 US、CN 等
            } catch (error) {
                console.error('IP地理位置获取失败:', error);
                return null;
            }
        };

        // 优先使用IP地理位置，如果失败则使用浏览器语言
        // const region = await getIPRegion() || getBrowserRegion();
        const region = getBrowserRegion();
        return region;
    } catch (error) {
        console.error('地区信息获取失败:', error);
        return null;
    }
};

export const trackGoogleEvent = async (event_name, data={}) => {
    // const sendTo = getEventMapping(event_name, location.host);
    const region = await getRegion();
    if (typeof window !== 'undefined' && window.gtag) {
        const formData = new FormData();
        formData.append('project', 'gamyeap');
        formData.append('evtime', dayjs().format('YYYY-MM-DD HH:mm:ss')); // 事件时间
        formData.append('evts', Date.now().toString()); // 时间戳
        formData.append('url_info', location.href); // URL参数
        formData.append('region', region);
        formData.append('uid', getLocalStorage('userInfo')?.user_id || ''); // UID
        formData.append('ip', getLocalStorage('client_ip') || ''); // IP
        formData.append('type', 'normal_event');
        formData.append('event_data', JSON.stringify(data));
        // 添加浏览器信息
        const browserInfo = getBrowserInfo();
        for (const [key, value] of Object.entries(browserInfo)) {
            formData.append(key, value);
        }

        // 添加事件数据
        for (const [key, value] of Object.entries(data)) {
            formData.append(key, value);
        }

        const endpoint = '/log/report';
        if (navigator.sendBeacon) {
            navigator.sendBeacon(endpoint, formData);
        } else if (window.fetch) {
            fetch(endpoint, {
                method: 'POST',
                body: formData,
                keepalive: true
            }).catch(error => console.error('Error sending data:', error));
        } else {
            // 降级到图片 Pixel 方法
            const img = new Image();
            const params = new URLSearchParams(formData).toString();
            img.src = `${endpoint}?${params}`;
        }

        // 发送 gtag 事件
        gtag('event', event_name, Object.fromEntries(formData));
    } else {
        console.log('Google Analytics is not loaded');
    }
};

export const pageInterview = page_name => {
    let userQuota = getLocalStorage('userQuota');
    let userInfo = getLocalStorage('userInfo');
    switch (page_name) {
        case '/':
            trackGoogleEvent('homepage_interview', {
                open_method: 'external_url',
                login_status: userInfo?.token ? 'logged' : 'no_logged'
            });
            break;
        case 'account':
            trackGoogleEvent('account_interview', { subscribe_status: 'no_subscribe' });
            break;
        case 'records':
            trackGoogleEvent('billingrecord_interview', { interview: 1 });
            break;
        case 'app':
            trackGoogleEvent('create_interview', { login_status: userInfo?.token ? 'logged' : 'no_logged' });
            break;
        case 'library':
            trackGoogleEvent('library_interview', { login_status: userInfo?.token ? 'logged' : 'no_logged' });
            break;
        case 'price':
            trackGoogleEvent('pricing_interview', {
                front_page: Math.random() > 0.3 ? 'app' : 'home',
                front_button: Math.random() > 0.3 ? 'upgrade' : 'price',
                subscribe_status: userQuota?.is_subscribled ? 'subscribed' : 'no_subscribe'
            });
            break;
        case 'buy':
            trackGoogleEvent('purchase_interview', {
                front_page: Math.random() > 0.3 ? 'app' : 'home',
                front_button: Math.random() > 0.3 ? 'upgrade' : 'price',
                subscribe_status: userQuota?.is_subscribled ? 'subscribed' : 'no_subscribe'
            });
            break;
        default:
            break;
    }
};

export default {
    initGoogleAnalytics,
    trackGoogleEvent
};
